import type { RosettaSDK } from "@product/rosetta-sdk";
import type { ScmpUser } from "@product/scmp-account-sdk";
import type { ReactNode } from "react";
import React, { createContext, useContext, useMemo, useState } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import type { Environment } from "react-relay";
import type { GeneralConfig } from "scmp-sdk/lib/config";
import type {
  GA4Event,
  LoginImpressionEventParameters,
} from "scmp-sdk/lib/google-analytics/ga4-types";
import type { MirrorEvents } from "scmp-sdk/lib/mirror/types";
import { createEnvironment } from "scmp-sdk/lib/relay";
import { createContextValueWarningProxy } from "scmp-sdk/lib/utils";

import type { GTMEvent } from "shared/lib/gtag/types";
import type { PageType } from "shared/lib/page-type";
import { getWindow } from "shared/lib/utils";

import type { AcquisitionNewsletter } from "./acquisition-popup-container/display/popups/acquisition-newsletter/types";
import type { Campaign } from "./acquisition-popup-container/display/popups/campaign/types";
import type { MyNews } from "./acquisition-popup-container/display/popups/my-news/types";
import type { OptInReminder } from "./acquisition-popup-container/display/popups/opt-in-reminder/types";
import type { TopicPackageFollow } from "./acquisition-popup-container/display/popups/topic-package-follow/types";
import type { WhyRegister } from "./acquisition-popup-container/display/popups/why-register/types";
import type { WidgetConfigItems } from "./acquisition-popup-container/types";
import type { BookmarkAction } from "./bookmark-button/enums";
import type { FollowAction, FollowType } from "./follow-button/enums";

export enum RouteType {
  Article = "Article",
  Page = "Page",
  Section = "Section",
  Topic = "Topic",
}

export const scmpPlus = {
  article: {
    paywallTypeId: "716f570e-3083-4138-a080-47d3830fafe3",
  },
  topicType: {
    newsAgenda: "news_agenda",
  },
};

export type AcquisitionContextValue = {
  popupState: PopupState;
  setPopupState: (value: PopupState) => void;
};

export type ExtraOptions = {
  applicationId?: string;
  entityId?: string;
  entityUuid?: string;
  isExternal?: boolean;
  query?: Record<string, string>;
  type?: RouteType;
};

export type PopupState = {
  acquisitions: Record<string, AcquisitionNewsletter>;
  campaigns: Record<string, Campaign>;
  myNews: Partial<MyNews> & {
    config?: WidgetConfigItems["my_news"];
  };
  optInReminders: Record<string, OptInReminder>;
  topicPackageFollows: Record<string, TopicPackageFollow>;
  whyRegister: Record<string, WhyRegister>;
};

type OpenLoginDialogParameters = {
  description?: string;
  destination: string;
  ga4CustomParameter: LoginImpressionEventParameters;
  title?: string;
};

export function parseRouteType(value?: string): RouteType | undefined {
  if (!Object.values(RouteType).includes(value as RouteType)) return;
  return value as RouteType;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const routerEvents: readonly [
  "routeChangeStart",
  "beforeHistoryChange",
  "routeChangeComplete",
  "routeChangeError",
  "hashChangeStart",
  "hashChangeComplete",
];
export type MittEmitter<T> = {
  emit(type: T, ...events: []): void;
  off(type: T, handler: Handler): void;
  on(type: T, handler: Handler): void;
};
export type RouterEvent = (typeof routerEvents)[number];
export type SdkFunction = Omit<
  ContextValue,
  "acquisition" | "currentRouteWithoutQuerystring" | "v1SignedUpNewsletters"
>;
type ContextValue = {
  acquisition: AcquisitionContextValue;
  bookmark: {
    check: (payload: { entityId: string }) => boolean;
    update: (payload: {
      action: BookmarkAction;
      entityId: string;
      trackings: {
        ga4Events: Record<"click", GA4Event>;
      };
    }) => void;
  };
  config: GeneralConfig;
  currentArticleId?: string;
  currentArticleUuid?: string;
  currentPageType: PageType;
  currentRoute: string;
  currentRouteWithoutQuerystring: string;
  environment?: Environment;
  handleCheckIsFollowed: (payload: { entityId: string; followType: FollowType }) => boolean;
  handleFollow: (payload: {
    action: FollowAction;
    callback?: () => void;
    entityIds: string[];
    followType: FollowType;
  }) => void;
  handleOpenLoginDialog: (parameters: OpenLoginDialogParameters) => void;
  handleRemoveRouteQuery: (keys: string[]) => Promise<void>;
  handleRoutePush: (url: string, extraOption?: ExtraOptions) => Promise<void>;
  handleTrackGA4: (event: GA4Event) => void;
  handleTrackGTM: (event?: GTMEvent) => void;
  handleTrackMirror: (event?: MirrorEvents) => void;
  hasAcceptedGDPR?: boolean;
  hasEnoughFollowedItems: boolean;
  hasWhyRegisterPPTOHomepagePositionIntersected: boolean;
  homeAsPath: string;
  initialHasEnoughFollowedItems: boolean;
  isInlinePaywallArticle?: boolean;
  isShowSubscription: boolean;
  isSubscriber?: boolean;
  rosettaInstance?: RosettaSDK;
  /**
   *  Require the value as the clear up effect is depends on this value (Suggest to use the cookie directly)
   */
  router: NextRouter;
  user?: ScmpUser;
  userCountry?: string;
  userTypeIds?: string[];
};

type Handler = (...events: []) => void;

type NextRouter = {
  get asPath(): string;
  events: MittEmitter<RouterEvent>;
};

const SdkContext = createContext<ContextValue>(createContextValueWarningProxy("SdkContext"));

export const SdkContextProvider: React.FunctionComponent<{
  children?: ReactNode;
  value: SdkFunction;
}> = ({ children, value }) => {
  const [popupState, setPopupState] = useState<PopupState>({
    acquisitions: {},
    campaigns: {},
    myNews: {},
    optInReminders: {},
    topicPackageFollows: {},
    whyRegister: {},
  });

  const acquisition = useMemo<AcquisitionContextValue>(
    () => ({
      popupState,
      setPopupState,
    }),
    [popupState],
  );

  const currentRouteWithoutQuerystring = useMemo(() => {
    try {
      return new URL(value.currentRoute, getWindow()?.location.origin).pathname;
    } catch {
      return "";
    }
  }, [value.currentRoute]);

  return (
    <SdkContext.Provider
      value={{
        ...value,
        acquisition,
        currentRouteWithoutQuerystring,
      }}
    >
      <GoogleReCaptchaProvider reCaptchaKey={value.config.recaptcha.siteKey}>
        {children}
      </GoogleReCaptchaProvider>
    </SdkContext.Provider>
  );
};

SdkContextProvider.displayName = "SdkContextProvider";

export const useSdkContext = () => {
  const { config, environment, ...props } = useContext(SdkContext);
  validateUrl("facebookLikeUrl", config?.facebook.likeUrl);
  validateUrl("accountsHost", config?.accounts.host);
  validateUrl("graphqlHost", config?.graphql.host);
  validateUrl("subscribeHost", config?.subscribe.host);
  return {
    ...props,
    config,
    environment:
      environment ??
      createEnvironment({
        authOptions: {
          allowEmptyToken: true,
        },
        urlOptions: {
          headers: {
            apikey: config.graphql.apiKey,
          },
          method: "POST",
          url: config.graphql.host,
        },
      }),
  };
};

const validateUrl = (title: string, url?: string) => {
  if (!url) return;
  try {
    new URL(url);
  } catch {
    console.error(`${title} is not a valid url format, value ${url}`);
  }
};
